import { useCallback, useEffect, useState } from "../imports";
import { useClientPrincipal } from "../context/ClientPrincipalContext";

export type EventRole = "EVENT_ADMIN" | "EVENT_CHECKIN" | "EVENT_VIEW";

export const useRoles = () => {
  const { clientPrincipal, isLoading: isAuthLoading } = useClientPrincipal();
  const [roles, setRoles] = useState<EventRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    // Get userId from clientPrincipal or sessionStorage (for local auth)
    const userId =
      clientPrincipal?.userId ??
      sessionStorage.getItem("userOid") ??
      (process.env.REACT_APP_USE_LOCAL_AUTH === "true" ? "test-user-id" : undefined);

    if (!userId) {
      setRoles([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch("/api/me", {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "client-principal-user-id": userId,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch roles: ${response.statusText}`);
      }

      const data = await response.json();
      setRoles(data.roles || []);
    } catch (err) {
      console.error("Error fetching user roles:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch roles");
      setRoles([]);
    } finally {
      setIsLoading(false);
    }
  }, [clientPrincipal?.userId]);

  useEffect(() => {
    if (!isAuthLoading) {
      fetchRoles();
    }
  }, [isAuthLoading, fetchRoles]);

  const hasRole = useCallback(
    (role: EventRole): boolean => {
      return roles.includes(role);
    },
    [roles]
  );

  const hasEventAdminRole = useCallback((): boolean => {
    return roles.includes("EVENT_ADMIN");
  }, [roles]);

  return {
    roles,
    isLoading,
    error,
    hasRole,
    hasEventAdminRole,
  };
};
